import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { getCompanies, deleteCompany, getCompanyLogoUrl } from "@/services/firebase/company";
import { getDramas } from "@/services/firebase/drama";
import { Company } from "@/types/company";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { useDebounce } from "@/hooks/useDebounce";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";

// 製作公司 Logo 組件 — 同步 build URL,瀏覽器自動 disk-cache,無需 useEffect
function CompanyLogo({ company }: { company: Company }) {
  const logoUrl = getCompanyLogoUrl(company.company_logo);

  if (!logoUrl) {
    return (
      <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
        <Building2 className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={company.company_name}
      width={64}
      height={64}
      loading="lazy"
      decoding="async"
      className="w-16 h-16 object-cover rounded-lg bg-muted"
    />
  );
}

const columns: AdminTableColumn[] = [
  { key: "logo", label: "Logo" },
  { key: "company_name", label: "公司名稱", sortable: true },
  { key: "drama_count", label: "劇集數", sortable: true, sortKey: "_drama_count" },
  { key: "website", label: "網站", hideBelow: "md" },
  { key: "created_at", label: "建立時間", hideBelow: "lg", sortable: true },
  { key: "actions", label: "操作" },
];

export default function CompanyListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    name: string;
  }>({ isOpen: false, id: null, name: "" });

  const { data: companies, isLoading, error } = useQuery({
    queryKey: ["companies"],
    queryFn: () => getCompanies(),
  });

  // 載入所有劇集以計算每間公司的劇集數
  const { data: allDramas } = useQuery({
    queryKey: ["dramas"],
    queryFn: () => getDramas(),
  });

  const dramaCountByCompany = useMemo(() => {
    const map = new Map<string, number>();
    if (!allDramas) return map;
    for (const d of allDramas) {
      if (d.company) {
        map.set(d.company, (map.get(d.company) || 0) + 1);
      }
    }
    return map;
  }, [allDramas]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
      setDeleteError(null);
    },
    onError: (error) => {
      console.error("刪除製作公司失敗:", error);
      setDeleteError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
    },
  });

  // 過濾和搜尋；同時注入 _drama_count 供排序使用
  const filteredCompanies = useMemo(() => {
    if (!companies || companies.length === 0) return [];

    return companies
      .filter((company) => {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const matchesSearch =
          !searchLower ||
          company.id.toLowerCase().includes(searchLower) ||
          (company.company_id && company.company_id.toLowerCase().includes(searchLower)) ||
          (company.company_name && company.company_name.toLowerCase().includes(searchLower)) ||
          (company.description && company.description.toLowerCase().includes(searchLower));

        return matchesSearch;
      })
      .map((c) => ({ ...c, _drama_count: dramaCountByCompany.get(c.id) || 0 }));
  }, [companies, debouncedSearchTerm, dramaCountByCompany]);

  const handleDelete = (id: string, name: string) => {
    setDeleteConfirm({ isOpen: true, id, name });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="製作公司" createHref="/companies/new" createLabel="新增製作公司" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="製作公司" createHref="/companies/new" createLabel="新增製作公司" />
        <AdminErrorAlert
          message={`載入製作公司時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      {deleteError && (
        <AdminErrorAlert message={deleteError!} onDismiss={() => setDeleteError(null)} />
      )}

      <AdminPageHeader title="製作公司" createHref="/companies/new" createLabel="新增製作公司" />

      <AdminSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="搜尋公司 ID、名稱或描述..."
      />

      <div className="mt-4">
        <AdminDataTable
          columns={columns}
          data={filteredCompanies}
          getRowId={(item) => item.id}
          onRowClick={(company) => navigate(`/companies/${company.id}`)}
          emptyTitle={searchTerm ? "沒有符合條件的製作公司" : "尚無製作公司資料"}
          emptyActionHref={!searchTerm ? "/companies/new" : undefined}
          emptyActionLabel={!searchTerm ? "新增第一個製作公司" : undefined}
          renderCell={(company, col) => {
            switch (col.key) {
              case "logo":
                return <CompanyLogo company={company} />;
              case "company_name":
                return (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {company.company_name || "-"}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground mt-0.5">
                      {company.company_id || company.id}
                    </span>
                  </div>
                );
              case "drama_count":
                return (
                  <span className="text-sm font-medium tabular-nums">
                    {company._drama_count}
                  </span>
                );
              case "website":
                return company.website ? (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {company.website}
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                );
              case "created_at":
                return (
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {company.created_at
                      ? new Date(company.created_at).toLocaleString("zh-TW")
                      : "-"}
                  </span>
                );
              case "actions":
                return (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                      actions={[
                        {
                          label: "編輯",
                          onClick: () => navigate(`/companies/${company.id}/edit`),
                        },
                        {
                          label: "刪除",
                          onClick: () => handleDelete(company.id, company.company_name || company.id),
                          disabled: deleteMutation.isPending,
                          variant: "danger",
                        },
                      ]}
                    />
                  </div>
                );
              default:
                return null;
            }
          }}
        />
      </div>

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除製作公司「${deleteConfirm.name}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: "" })}
      />
    </AdminPage>
  );
}
